import React from "react";
import { Box, Typography, styled } from "@mui/material";
import { Check2Icon, PlanIcon } from "../../assets/icons";
import { PlanItemResponse } from "../../openapi/client";
import { useMealCardData } from "./useMealCardData";

const MealCard = styled(Box)`
  background: white;
  border-radius: 16px;
  border: 1px solid var(--base-100);
  box-shadow: 0 2px 0 0 var(--base-100);
  width: 85vw;
  min-width: 85vw;
  flex-shrink: 0;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  transform-origin: center center;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;

  &:focus-visible {
    outline: 2px solid rgba(41, 188, 98, 0.5);
    outline-offset: 2px;
  }
  &:hover {
    border-color: var(--base-300);
    box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.04), 0px 2px 0px 0px var(--base-300);
  }
`;

interface MobileMealCardProps {
  meal?: PlanItemResponse;
  title?: string;
  subtitle?: string;
  features?: string[];
  price?: string;
  partner?: string;
  imageUrl?: string;
}

export const MobileMealCard: React.FC<MobileMealCardProps> = (props) => {
  const { cardTitle, cardSubtitle, cardMealTypes, cardPrice, cardPartner, cardImageUrl, cardFeatures, buyHandler, t } =
    useMealCardData(props);

  return (
    <MealCard onClick={buyHandler}>
      <Box
        sx={{
          padding: "12px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          height: "100%",
        }}
      >
        {/* Mobile Layout: Horizontal */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "12px",
          }}
        >
          {/* Image - Left side */}
          <Box
            sx={{
              position: "relative",
              width: "90px",
              height: "90px",
              borderRadius: "12px",
              backgroundImage: `url(${cardImageUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              flexShrink: 0,
            }}
          />

          {/* Title, Subtitle, Partner Badge - Right side */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1, justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "16px",
                  lineHeight: "normal",
                  color: "var(--font-dark)",
                }}
              >
                {cardTitle}
              </Typography>
              {cardSubtitle && (
                <Typography
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    fontFamily: "Inter",
                    fontWeight: 500,
                    fontSize: "12px",
                    lineHeight: "18px",
                    color: "var(--font-default)",
                  }}
                >
                  {cardSubtitle}
                </Typography>
              )}
            </Box>

            {/* Partner Badge */}
            <Box
              sx={{
                background: "var(--system-body-bg, #ffffff)",
                borderRadius: "6px",
                padding: "4px 8px",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                alignSelf: "flex-start",
                boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Box
                sx={{
                  width: "16px",
                  height: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--success-default)",
                  "& svg": {
                    width: "16px",
                    height: "16px",
                    fill: "var(--success-default)",
                  },
                }}
              >
                <PlanIcon />
              </Box>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "12px",
                  lineHeight: "1.5",
                  color: "var(--success-default)",
                }}
              >
                {cardPartner}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Details Container */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", px: "4px", flexGrow: 1 }}>
          {/* Features Section */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", py: "4px", flexGrow: 1 }}>
            {cardFeatures.map((feature, index) => (
              <Box key={index} sx={{ display: "flex", alignItems: "start", gap: "4px" }}>
                <Box
                  sx={{
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    color: "var(--success-default)",
                    "& svg": {
                      width: "20px",
                      height: "20px",
                      fill: "var(--success-default)",
                    },
                  }}
                >
                  <Check2Icon />
                </Box>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "1.5",
                    color: "var(--font-default)",
                    flex: 1,
                  }}
                >
                  {feature}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Bottom Section - Divider and Price (aligned at bottom) */}
          <Box sx={{ mt: "auto" }}>
            {/* Divider */}
            <Box
              sx={{
                height: "1px",
                width: "100%",
                background: "var(--base-100)",
              }}
            />

            {/* Price Section */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: "7px",
                lineHeight: "normal",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "12px",
                  color: "var(--font-light)",
                }}
              >
                {t("startingFrom")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "2px",
                  whiteSpace: "nowrap",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 700,
                    fontSize: "20px",
                    color: "var(--success-dark)",
                    letterSpacing: "-0.4px",
                  }}
                >
                  {cardPrice}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 400,
                    fontSize: "14px",
                    color: "var(--success-dark)",
                  }}
                >
                  {t("currency")}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 400,
                    fontSize: "14px",
                    color: "var(--font-default)",
                  }}
                >
                  / {t("day")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </MealCard>
  );
};
